import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * ClientPortalMissingItems displays missing items that are client-visible
 * and allows clients to mark items as received
 */
const ClientPortalMissingItems = ({ projectId }) => {
  const [missingItems, setMissingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (projectId) {
      fetchMissingItems();
    }
  }, [projectId]);
  
  const fetchMissingItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use axios directly with the JWT token from localStorage (set in header by ClientDashboard)
      console.log(`Fetching missing items for project: ${projectId}`);
      
      const response = await axios.get(`/api/client-portal/projects/${projectId}/missing-items`);
      
      if (response.data && response.data.missing_items) {
        setMissingItems(response.data.missing_items);
        console.log(`Received ${response.data.missing_items.length} missing items`);
      } else {
        setMissingItems([]);
        console.log('No missing items received');
      }
    } catch (err) {
      console.error('Error fetching missing items:', err);
      // Don't show the error to users, just set an empty array
      setMissingItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkReceived = async (id) => {
    try {
      // Use axios directly with JWT token
      console.log(`Marking item ${id} as received`);
      
      const response = await axios.put(`/api/client-portal/missing-items/${id}/received`);
      
      if (response.data && response.data.missing_item) {
        // Update the item in the list
        setMissingItems(missingItems.map(item => 
          item.id === id ? response.data.missing_item : item
        ));
        console.log('Item successfully marked as received');
      }
    } catch (err) {
      console.error('Error updating missing item status:', err);
      alert('Failed to update status. Please try again.');
    }
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Calculate if an item is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Required Items</h2>
      </div>
      
      <div className="space-y-3">
        {missingItems.map((item) => (
          <div 
            key={item.id} 
            className={`p-4 rounded-md ${
              item.status === 'received' ? 'bg-green-50 border border-green-100' : 
              isOverdue(item.due_date) && item.status === 'pending' ? 'bg-red-50 border border-red-100' : 'bg-gray-50 border border-gray-100'
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  item.status === 'received' ? 'line-through text-gray-500' : 'text-gray-900'
                }`}>
                  {item.content}
                </p>
                <div className="mt-2 flex items-center text-xs text-gray-500">
                  <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                  {formatDate(item.due_date)}
                  {isOverdue(item.due_date) && item.status === 'pending' && (
                    <span className="ml-2 text-xs font-semibold text-red-600">
                      OVERDUE
                    </span>
                  )}
                  <span className={`ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    item.status === 'received' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status === 'received' ? 'Received' : 'Pending'}
                  </span>
                </div>
              </div>
              {item.status === 'pending' && (
                <button
                  onClick={() => handleMarkReceived(item.id)}
                  className="ml-4 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Mark as Provided
                </button>
              )}
            </div>
          </div>
        ))}
        
        {missingItems.length === 0 && (
          <div className="p-4 text-center bg-gray-50 rounded-md border border-gray-200">
            <p className="text-gray-500">No pending items needed from you at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortalMissingItems; 